'use client';

import { useState, useEffect } from 'react';

export default function CicilanDashboard() {
  const [searchCriteria, setSearchCriteria] = useState({
    userSalary: '',
    maxTenor: '6'
  });
  const [formattedSalary, setFormattedSalary] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [groupedResults, setGroupedResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    fetchSummary();
  }, []);

  // Update formatted salary when userSalary changes
  useEffect(() => {
    if (searchCriteria.userSalary) {
      setFormattedSalary(formatSalaryInput(searchCriteria.userSalary));
    }
  }, [searchCriteria.userSalary]);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);
    } catch (err) {
      console.error('Error fetching summary:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      if (!searchCriteria.userSalary || !searchCriteria.maxTenor) {
        alert('Please enter both salary and maximum tenor');
        return;
      }

      const params = new URLSearchParams({ action: 'find-best' });
      params.append('userSalary', searchCriteria.userSalary);
      params.append('maxTenor', searchCriteria.maxTenor);

      const response = await fetch(`/api/cicilan?${params}`);
      const data = await response.json();
      const results = data.bestOptions || [];
      
      // Group results by tenor (month)
      const grouped = results.reduce((acc, option) => {
        const tenor = option.tenor;
        if (!acc[tenor]) {
          acc[tenor] = [];
        }
        acc[tenor].push(option);
        return acc;
      }, {});

      setSearchResults(results);
      setGroupedResults(grouped);

      // Calculate affordability
    } catch (err) {
      console.error('Error searching:', err);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    // Use dots as thousand separators for Indonesian format
    return `Rp ${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  };

  const formatSalaryInput = (value) => {
    // Remove all non-digits
    const numbers = value.replace(/[^\d]/g, '');
    // Add dots as thousand separators
    return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const handleSalaryChange = (e) => {
    const value = e.target.value;
    // Remove all non-digits for the actual value
    const numericValue = value.replace(/[^\d]/g, '');
    // Update both the raw value and formatted display
    setSearchCriteria(prev => ({ ...prev, userSalary: numericValue }));
    setFormattedSalary(formatSalaryInput(value));
  };

  // Prevent hydration mismatch by not rendering until mounted
  if (!mounted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading data cicilan...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center text-red-600">
          <h2 className="text-2xl font-bold mb-4">Error</h2>
          <p>{error}</p>
          <button 
            onClick={fetchSummary}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Ulang
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Cicilan FPK Dashboard</h1>
        </div>

        {/* Summary Cards */}
        {/* {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500">Total Opsi</h3>
              <p className="mt-2 text-3xl font-bold text-blue-600">
                {summary.totalOptions}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500">Tenor Max</h3>
              <p className="mt-2 text-3xl font-bold text-green-600">
                {summary.tenorMax} <span className="text-lg text-gray-500">bulan</span>
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500">Minimal Gaji Pokok</h3>
              <p className="mt-2 text-2xl font-bold text-purple-600">
                {formatCurrency(summary.minimalGajiPokok)}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500">Max Plafond</h3>
              <p className="mt-2 text-2xl font-bold text-orange-600">
                {formatCurrency(summary.plafondMax)}
              </p>
            </div>
          </div>
        )} */}

        {/* Search Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Cari Cicilan yang bisa diambil</h2>
          <p className="text-gray-600 mb-6">Masukkan gaji dan maksimal tenor untuk melihat opsi cicilan yang sesuai</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gaji Anda (Rp) *
              </label>
              <input
                type="text"
                value={formattedSalary}
                onChange={handleSalaryChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="Contoh: 5.000.000"
              />
              <p className="text-xs text-gray-500 mt-1">Masukkan gaji bulanan Anda (contoh: 5.000.000)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maksimal Tenor : (Sisa Kontrak) {searchCriteria.maxTenor} bulan *
              </label>
              
              {/* Range Slider */}
              <input
                type="range"
                min="1"
                max="12"
                value={searchCriteria.maxTenor}
                onChange={(e) => setSearchCriteria(prev => ({ ...prev, maxTenor: e.target.value }))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer mb-3"
                style={{
                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((searchCriteria.maxTenor - 1) / 11) * 100}%, #e5e7eb ${((searchCriteria.maxTenor - 1) / 11) * 100}%, #e5e7eb 100%)`
                }}
              />
              
              {/* Manual Input */}
              <input
                type="number"
                min="1"
                max="12"
                value={searchCriteria.maxTenor}
                onChange={(e) => setSearchCriteria(prev => ({ ...prev, maxTenor: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="Atau ketik manual: 1-12"
              />
              <p className="text-xs text-gray-500 mt-1">Geser slider atau ketik manual (1-12 bulan)</p>
            </div>
          </div>
          
          <button
            onClick={handleSearch}
            disabled={!searchCriteria.userSalary || !searchCriteria.maxTenor}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Cari Opsi Cicilan
          </button>

          {/* Affordability Info */}
          {/* {affordability && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Affordability Analysis
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-blue-700">Affordable Options</p>
                  <p className="text-xl font-bold text-blue-900">
                    {affordability.affordableOptions} / {affordability.totalOptions}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-blue-700">Affordability Rate</p>
                  <p className="text-xl font-bold text-blue-900">
                    {affordability.percentage}%
                  </p>
                </div>
                <div>
                  <p className="text-sm text-blue-700">Max Affordable Plafond</p>
                  <p className="text-xl font-bold text-blue-900">
                    {formatCurrency(affordability.maxAffordablePlafond)}
                  </p>
                </div>
              </div>
            </div>
          )} */}

          {/* Search Results Grouped by Month */}
          {Object.keys(groupedResults).length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Opsi Cicilan yang Tersedia ({searchResults.length} total opsi)
              </h3>
              
              {Object.keys(groupedResults)
                .sort((a, b) => parseInt(a) - parseInt(b))
                .map(tenor => (
                <div key={tenor} className="mb-6">
                  <h4 className="text-md font-semibold text-blue-900 mb-3 bg-blue-50 p-3 rounded-t-lg">
                    Tenor {tenor} Bulan ({groupedResults[tenor].length} opsi)
                  </h4>
                  
                  <div className="overflow-x-auto border border-gray-200 rounded-b-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Plafond
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Angsuran/Bulan
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Diterima
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Biaya Admin
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Min Gaji
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {groupedResults[tenor]
                          .sort((a, b) => a.plafond - b.plafond)
                          .map((option, index) => (
                          <tr key={option.id || index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatCurrency(option.plafond)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-green-600 font-semibold">
                              {formatCurrency(option.angsuranBulan)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(option.nominalDiterima)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(option.biayaAdmin)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(option.minimalGajiPokok)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Catatan:</strong> Semua opsi di atas sesuai dengan gaji Anda ({formatCurrency(parseInt(searchCriteria.userSalary))}) 
                  dan maksimal tenor {searchCriteria.maxTenor} bulan yang Anda pilih.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
